import { format } from 'date-fns';
import { circleBilletOrange, circlePixOrange } from '@ibk/shared-assets';
import { FeatureFlagUseCase, useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { formatAsTwoLinesBarcode, formatCurrency, formatPercent, formatShortDateWithoutTimezone, } from '@ibk/shared-utils';
import { TransactionFlow } from '../../enums';
const INVALID_AGENCY = '0000';
const INVALID_ACCOUNT = '*';
function getTransactionDate(transactionAt, transactionFlow) {
    if (!transactionAt) {
        return undefined;
    }
    if (transactionFlow === TransactionFlow.SCHEDULED) {
        return format(transactionAt, 'dd/MM/yyyy');
    }
    return format(transactionAt, 'dd/MM/yyyy HH:mm');
}
export function useFormatReceipt() {
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isBaasV2Enabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const { t, currentLanguage } = useTranslation();
    const { maskDocumentHidden } = useCnpjAlphanumeric();
    function getTitle(transactionFlow) {
        if (transactionFlow === TransactionFlow.SCHEDULED) {
            return t('scheduling');
        }
        return t('receipt');
    }
    function getTransactionStatus(transactionFlow) {
        if (transactionFlow === TransactionFlow.SCHEDULED) {
            return t('scheduled');
        }
        return t('done');
    }
    function getDestinyAgency(bankAgency) {
        if (bankAgency && bankAgency !== INVALID_AGENCY) {
            return bankAgency;
        }
    }
    function getDestinyAccount(bankAccountNumber) {
        if (bankAccountNumber && !bankAccountNumber.includes(INVALID_ACCOUNT)) {
            return bankAccountNumber;
        }
    }
    function getDocument(document) {
        if (!document)
            return undefined;
        return maskDocumentHidden(document);
    }
    function getBaseProperties(flow, amount, image) {
        return {
            title: getTitle(flow),
            amount: formatCurrency(amount ?? 0),
            image: image,
        };
    }
    function getTransactionInfos(transactionType, transactionDate, transactionFlow) {
        return [
            { label: t('transaction-type'), value: transactionType },
            { label: t('transaction-date'), value: getTransactionDate(transactionDate, transactionFlow) },
            { label: t('transaction-status'), value: getTransactionStatus(transactionFlow) },
        ];
    }
    function getOriginInfos(bankName, name, document, bankAgency, bankAccountNumber) {
        return [
            { label: t('origin-bank'), value: bankName },
            { label: t('name'), value: name },
            { label: t('cpf-cnpj'), value: getDocument(document) },
            { label: t('agency'), value: bankAgency },
            { label: t('account'), value: bankAccountNumber },
        ];
    }
    function getDestinyInfos(bankName, name, document, bankAgency, bankAccountNumber, pixKey) {
        return [
            { label: t('destiny-bank'), value: bankName },
            { label: t('name'), value: name },
            { label: t('cpf-cnpj'), value: getDocument(document) },
            { label: t('agency'), value: getDestinyAgency(bankAgency) },
            { label: t('account'), value: getDestinyAccount(bankAccountNumber) },
            { label: t('pix-key'), value: pixKey },
        ];
    }
    function getIdentificationInfos(authentication, transactionId) {
        return [
            { label: t('authentication'), value: authentication },
            { label: t('transaction-id'), value: transactionId },
        ];
    }
    function fromBilletReceiptToReceipt(billetReceipt) {
        const isCashInOrReversal = billetReceipt.flow === TransactionFlow.CASH_IN || billetReceipt.flow === TransactionFlow.REVERSAL_IN;
        const isCashOutOrScheduled = billetReceipt.flow === TransactionFlow.CASH_OUT || billetReceipt.flow === TransactionFlow.SCHEDULED;
        const transactionType = billetReceipt.flow === TransactionFlow.REVERSAL_IN ? t('billet-reversal') : t('billet');
        const charges = !isBaasV2Enabled && billetReceipt.flow === TransactionFlow.CASH_OUT
            ? formatCurrency(billetReceipt.charges ?? 0)
            : undefined;
        let fees, fine;
        if (isBaasV2Enabled) {
            fees = formatCurrency(billetReceipt.fees ?? 0);
            fine = formatCurrency(billetReceipt.fine ?? 0);
        }
        else if (isCashInOrReversal) {
            fees = formatPercent(billetReceipt.fees ?? 0);
            fine = formatCurrency(billetReceipt.fine ?? 0);
        }
        const discounts = formatCurrency(billetReceipt.discounts ?? 0);
        return {
            ...getBaseProperties(billetReceipt.flow, billetReceipt.amount, circleBilletOrange),
            infos: [
                getTransactionInfos(transactionType, billetReceipt.transactionDate, billetReceipt.flow),
                [
                    { label: t('charges'), value: charges },
                    { label: t('fees'), value: fees },
                    { label: t('discount'), value: discounts },
                    { label: t('fine'), value: fine },
                ],
                [
                    { label: isCashOutOrScheduled ? t('receiver') : t('payer'), value: billetReceipt.receiverName },
                    { label: t('cpf-cnpj'), value: getDocument(billetReceipt.receiverIdentificationNumber) },
                    {
                        label: t('due-date'),
                        value: billetReceipt.dueDate && formatShortDateWithoutTimezone(billetReceipt.dueDate, currentLanguage),
                    },
                    { label: t('billet-code'), value: billetReceipt.barcode && formatAsTwoLinesBarcode(billetReceipt.barcode) },
                ],
                getIdentificationInfos(undefined, billetReceipt.id),
            ],
        };
    }
    function fromP2PReceiptToReceipt(p2pReceipt) {
        const transactionType = p2pReceipt.flow === TransactionFlow.REVERSAL_IN
            ? t('transfer-between-accounts-reversal')
            : t('transfer-between-accounts');
        return {
            ...getBaseProperties(p2pReceipt.flow, p2pReceipt.amount, circlePixOrange),
            infos: [
                getTransactionInfos(transactionType, p2pReceipt.dateTransaction),
                getOriginInfos(p2pReceipt.bankNameOrigin, p2pReceipt.nameOrigin, p2pReceipt.documentOrigin, p2pReceipt.branchOrigin, p2pReceipt.accountOrigin),
                getDestinyInfos(p2pReceipt.bankNameTarget, p2pReceipt.nameTarget, p2pReceipt.documentTarget, p2pReceipt.branchTarget, p2pReceipt.accountTarget),
                getIdentificationInfos(undefined, p2pReceipt.id),
            ],
        };
    }
    function fromTedReceiptToReceipt(tedReceipt) {
        return {
            ...getBaseProperties(TransactionFlow.CASH_IN, tedReceipt.amount, circlePixOrange),
            infos: [
                getTransactionInfos(t('ted'), tedReceipt.dateTransaction),
                getOriginInfos(tedReceipt.originBank, tedReceipt.originName, tedReceipt.originDocument, tedReceipt.originAgency, tedReceipt.originAccount),
                getDestinyInfos(tedReceipt.destinyBank, tedReceipt.destinyName, tedReceipt.destinyDocument, tedReceipt.destinyAgency, tedReceipt.destinyAccount),
                getIdentificationInfos(undefined, tedReceipt.id),
            ],
        };
    }
    function fromPixReceiptToReceipt(pixReceipt) {
        const isPixReversal = pixReceipt.flow === TransactionFlow.REVERSAL_IN || pixReceipt.flow === TransactionFlow.REVERSAL_OUT;
        const transactionType = isPixReversal ? t('pix-reversal') : t('transfer-pix');
        return {
            ...getBaseProperties(pixReceipt.flow, pixReceipt.amount, circlePixOrange),
            infos: [
                getTransactionInfos(transactionType, pixReceipt.transactionAt, pixReceipt.flow),
                getOriginInfos(pixReceipt.origin?.bankName, pixReceipt.origin?.name, pixReceipt.origin?.document, pixReceipt.origin?.bankAgency, pixReceipt.origin?.bankAccountNumber),
                getDestinyInfos(pixReceipt.destiny?.bankName, pixReceipt.destiny?.name, pixReceipt.destiny?.document, pixReceipt.destiny?.bankAgency, pixReceipt.destiny?.bankAccountNumber),
                getIdentificationInfos(pixReceipt.authentication, pixReceipt.id),
            ],
        };
    }
    return {
        fromPixReceiptToReceipt,
        fromTedReceiptToReceipt,
        fromP2PReceiptToReceipt,
        fromBilletReceiptToReceipt,
    };
}
